import { WritableLexicalEditor } from '../../apps/professional-app/fa/MeetingSummary/MeetingResults/Lexical/WritableLexicalEditor/WritableLexicalEditor'
import React, { useEffect, useMemo, useState } from 'react'
import { $getRoot, $getSelection, $getState, $isElementNode, $isRangeSelection, EditorState, TextNode } from 'lexical'
import { MentionsPlugin } from '../../apps/professional-app/fa/MeetingSummary/MeetingResults/Lexical/Plugins/MentionPlugin/MentionsPlugin'
import { Button } from '../Button/Button'
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext'
import { getEditorStateFromElements, INPUT_WITH_TOKEN_NODES, InputWithTokensElements } from './utils'
import { $isMentionNode, MentionData, mentionState } from '../../apps/professional-app/fa/MeetingSummary/MeetingResults/Lexical/Nodes/MentionNode/MentionNode'

export interface InputWithTokenProps {
  options: MentionData[]
  label?: string
  onChange?: (parsedContent: InputWithTokensElements[]) => void
  elements?: InputWithTokensElements[]
}

export const InputWithTokens: React.FC<InputWithTokenProps> = ({ label, options, onChange, elements }) => {
  const initialEditorState = useMemo<EditorState | null>(() => getEditorStateFromElements(elements), [elements])
  const handleChange = (editorState: EditorState) => {
    editorState.read(() => {
      const root = $getRoot()
      const children = root.getChildren()
      const parsedData: InputWithTokensElements[] = []
      children.forEach((node, i) => {
        if ($isElementNode(node)) {
          const inlineNodes = node.getChildren() // casting for simplicity
          for (const node of inlineNodes) {
            if ($isMentionNode(node)) {
              const state = $getState(node, mentionState)
              parsedData.push({
                type: 'mention',
                content: node.getTextContent(),
                mentionData: state,
              })
            } else if (node instanceof TextNode) {
              parsedData.push({
                type: 'text',
                content: node.getTextContent(),
              })
            }
          }
        }

        if (i < children.length - 1) {
          parsedData.push({ type: 'line-break', content: '\n' })
        }
      })

      onChange?.(parsedData)
    })
  }

  return (
    <div>
      {label ? <div>{label}</div> : null}
      <div>
        <WritableLexicalEditor
          editorContainerClassName={'flex-grow-1'}
          onChange={handleChange}
          initialEditorState={initialEditorState}
          lexicalNodes={INPUT_WITH_TOKEN_NODES}
        >
          <MentionsPlugin options={options} />
          <OpenOptionsButton />
        </WritableLexicalEditor>
      </div>
    </div>
  )
}

const OpenOptionsButton = () => {
  const [editor] = useLexicalComposerContext()
  const [isEditorEmpty, setIsEditorEmpty] = useState(true)
  const handleOpenOptions = () => {
    editor.update(() => {
      const root = $getRoot()
      const selection = $getSelection() || root.selectEnd()
      if ($isRangeSelection(selection)) {
        const anchor = selection.anchor
        const currentText = anchor.getNode().getTextContent()
        const prevChar = currentText[anchor.offset - 1]
        const needsSpace = prevChar && /\S/.test(prevChar)
        selection.insertText(needsSpace ? ' @' : '@')
      }
    })
    editor.focus()
  }

  useEffect(() => {
    const removeListener = editor.registerUpdateListener(({ editorState }) => {
      editorState.read(() => {
        const textContent = $getRoot().getTextContent()
        if (textContent) {
          setIsEditorEmpty(false)
        } else {
          setIsEditorEmpty(true)
        }
      })
    })
    return () => {
      removeListener()
    }
  }, [editor])

  return (
    <div className='d-flex gap-2 align-items-center'>
      {isEditorEmpty ? <div className='paragraph-extra-small'>Press @ to open options</div> : null}
      <Button variant='primaryGhost' className='p-0' onClick={handleOpenOptions} type='button'>
        <i className='bi bi-plus' />
      </Button>
    </div>
  )
}
